import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { updateAddonStatus, ADDON_CATALOG } from "@/lib/contractAddons";
import { logActivity } from "@/lib/logActivity";
import Swal from "sweetalert2";
import type { Client } from "../types";

/* ─── Types ──────────────────────────────────────────────────── */
interface ContractAddonRow {
  id: string;
  addon_key: string;
  status: string;
  price: number;
  activation_mode: string;
  contract_id: string;
  source_label: "contract";
  contract_number?: string;
  contract_title?: string;
}

interface ClientAddonRow {
  id: string;
  addon_key: string;
  status: string;
  price: number;
  source: string | null;
  source_label: "client";
  notes: string | null;
}

type UnifiedAddon = ContractAddonRow | ClientAddonRow;

/* Merged view per addon_key */
interface AddonEntry {
  key: string;
  rows: UnifiedAddon[];
  /* computed */
  isActive: boolean;
  totalPrice: number;
}

/* ─── Related module navigation per addon_key ─────────────────── */
const MODULE_ROUTES: Record<string, (clientId: string) => string> = {
  digital_invitation: () => "/admin/invitations",
  signature_book:     () => "/admin/signature-books",
  photo_selection:    (id) => `/admin/jobs?client=${id}`,
  final_deliveries:   (id) => `/admin/jobs?client=${id}`,
  media_packages:     () => "/admin/media-packages",
  dvd_online:         () => "/admin/media-packages",
  appointments:       () => "/admin/appointments",
};

/* ─── Source badge ───────────────────────────────────────────── */
function SourceBadge({ row }: { row: UnifiedAddon }) {
  if (row.source_label === "contract") {
    const cr = row as ContractAddonRow;
    const mode = cr.activation_mode;
    const modeStyle =
      mode === "paid"  ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
      mode === "gift"  ? "bg-violet-50  text-violet-700  border-violet-200"  :
      mode === "promo" ? "bg-amber-50   text-amber-700   border-amber-200"   :
                         "bg-slate-100  text-slate-500   border-slate-200";
    return (
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${modeStyle} capitalize`}>
        {mode ?? "contract"}
      </span>
    );
  }
  const cl = row as ClientAddonRow;
  const src = cl.source ?? "manual";
  const srcStyle =
    src === "manual" ? "bg-slate-100 text-slate-500 border-slate-200" :
    src === "promo"  ? "bg-amber-50  text-amber-700  border-amber-200"  :
                       "bg-slate-100 text-slate-400  border-slate-200";
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${srcStyle} capitalize`}>
      {src}
    </span>
  );
}

/* ─── Props ──────────────────────────────────────────────────── */
interface Props {
  client: Client;
  tenantId: string;
}

/* ─── AddOnsTab ──────────────────────────────────────────────── */
export default function AddOnsTab({ client, tenantId }: Props) {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<AddonEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState<string | null>(null); // addon_key in progress

  /* ── Fetch ── */
  const fetchData = useCallback(async () => {
    setLoading(true);

    const [contractRes, clientRes] = await Promise.all([
      supabase
        .from("contract_addons")
        .select("id, addon_key, status, price, activation_mode, contract_id, contracts(contract_number, title)")
        .eq("tenant_id", tenantId)
        .eq("client_id", client.id)
        .order("created_at", { ascending: true }),
      supabase
        .from("client_addons")
        .select("id, addon_key, status, price, source, notes")
        .eq("tenant_id", tenantId)
        .eq("client_id", client.id)
        .order("created_at", { ascending: true }),
    ]);

    /* contract_addons rows */
    const contractRows: ContractAddonRow[] = (contractRes.data ?? []).map((r: Record<string, unknown>) => ({
      id:              r.id as string,
      addon_key:       r.addon_key as string,
      status:          r.status as string,
      price:           Number(r.price ?? 0),
      activation_mode: r.activation_mode as string,
      contract_id:     r.contract_id as string,
      source_label:    "contract" as const,
      contract_number: (r.contracts as { contract_number?: string } | null)?.contract_number,
      contract_title:  (r.contracts as { title?: string } | null)?.title,
    }));

    /* client_addons rows */
    const clientRows: ClientAddonRow[] = (clientRes.data ?? []).map((r: Record<string, unknown>) => ({
      id:           r.id as string,
      addon_key:    r.addon_key as string,
      status:       r.status as string,
      price:        Number(r.price ?? 0),
      source:       r.source as string | null,
      source_label: "client" as const,
      notes:        r.notes as string | null,
    }));

    /* Merge by addon_key — group all rows */
    const keyMap = new Map<string, UnifiedAddon[]>();
    [...contractRows, ...clientRows].forEach((row) => {
      const existing = keyMap.get(row.addon_key) ?? [];
      keyMap.set(row.addon_key, [...existing, row]);
    });

    /* Build entries for the 6 main addon keys in catalog order */
    const DISPLAY_KEYS = [
      "digital_invitation",
      "signature_book",
      "photo_selection",
      "final_deliveries",
      "dvd_online",
      "appointments",
    ];

    const result: AddonEntry[] = DISPLAY_KEYS.map((key) => {
      const rows = keyMap.get(key) ?? [];
      const isActive = rows.some((r) => r.status === "active");
      const totalPrice = rows.reduce((sum, r) => sum + r.price, 0);
      return { key, rows, isActive, totalPrice };
    });

    setEntries(result);
    setLoading(false);
  }, [client.id, tenantId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── Toggle disable/enable ── */
  const handleToggle = async (entry: AddonEntry, disable: boolean) => {
    const confirmResult = await Swal.fire({
      title: disable ? "Disable add-on?" : "Re-enable add-on?",
      text: disable
        ? "The client will lose access to this feature in their portal."
        : "The client will regain access to this feature.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: disable ? "Disable" : "Re-enable",
      cancelButtonText: "Cancel",
      background: "#fff",
      color: "#1e293b",
      confirmButtonColor: disable ? "#ef4444" : "#C8A96E",
      cancelButtonColor: "#94a3b8",
    });
    if (!confirmResult.isConfirmed) return;

    setWorking(entry.key);
    const newStatus = disable ? "disabled" : "active";
    const now = new Date().toISOString();

    // Update all rows for this addon_key
    const contractRows = entry.rows.filter((r) => r.source_label === "contract") as ContractAddonRow[];
    const clientRows   = entry.rows.filter((r) => r.source_label === "client")   as ClientAddonRow[];

    await Promise.all([
      ...contractRows.map((r) => updateAddonStatus(tenantId, r.id, newStatus as "active" | "disabled")),
      ...clientRows.map((r) =>
        supabase.from("client_addons")
          .update({ status: newStatus, updated_at: now })
          .eq("id", r.id)
          .eq("tenant_id", tenantId)
      ),
    ]);

    await logActivity({
      tenantId,
      action: disable ? "disabled" : "enabled",
      entity: "addon",
      entityId: client.id,
      entityName: `${entry.key} → ${client.full_name}`,
    });

    setWorking(null);
    fetchData();
    Swal.fire({
      title: disable ? "Disabled" : "Re-enabled",
      icon: "success",
      timer: 1500,
      showConfirmButton: false,
      background: "#fff",
      color: "#1e293b",
    });
  };

  /* ── Open module ── */
  const handleOpenModule = (entry: AddonEntry) => {
    const routeFn = MODULE_ROUTES[entry.key];
    if (routeFn) navigate(routeFn(client.id));
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 text-sm py-12 justify-center">
        <div className="w-4 h-4 border-2 border-slate-200 border-t-slate-400 rounded-full animate-spin"></div>
        Loading add-ons…
      </div>
    );
  }

  const activeCount  = entries.filter((e) => e.isActive).length;
  const totalRevenue = entries.reduce((sum, e) => sum + e.totalPrice, 0);

  return (
    <div className="space-y-5 max-w-3xl">

      {/* Summary row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-slate-400 text-xs mb-1">Active Add-ons</p>
          <p className="text-2xl font-bold text-slate-800">{activeCount}</p>
          <p className="text-slate-400 text-[10px] mt-0.5">of {entries.length} available</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-slate-400 text-xs mb-1">Total Charged</p>
          <p className="text-2xl font-bold text-[#9a7840]">
            ${totalRevenue.toLocaleString("en-US", { minimumFractionDigits: 0 })}
          </p>
          <p className="text-slate-400 text-[10px] mt-0.5">across all add-ons</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-slate-400 text-xs mb-1">Sources</p>
          <p className="text-2xl font-bold text-slate-800">
            {new Set(entries.flatMap((e) => e.rows.map((r) => r.source_label))).size}
          </p>
          <p className="text-slate-400 text-[10px] mt-0.5">contract / manual</p>
        </div>
      </div>

      {/* Add-on cards */}
      <div className="space-y-3">
        {entries.map((entry) => {
          const meta = ADDON_CATALOG.find((a) => a.key === entry.key);
          if (!meta) return null;

          const hasRows    = entry.rows.length > 0;
          const isWorking  = working === entry.key;

          return (
            <div
              key={entry.key}
              className={`bg-white rounded-xl border overflow-hidden transition-all ${
                entry.isActive
                  ? "border-slate-200"
                  : hasRows
                  ? "border-slate-200 opacity-75"
                  : "border-slate-100 opacity-50"
              }`}
            >
              {/* Header */}
              <div className="flex items-center gap-4 px-5 py-4">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${meta.bg} shrink-0`}>
                  <i className={`${meta.icon} text-lg ${meta.color}`}></i>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-slate-800 text-sm font-semibold">{meta.label}</p>
                    {/* Status badge */}
                    {entry.isActive ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-emerald-50 text-emerald-600 border-emerald-200">
                        Active
                      </span>
                    ) : hasRows ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-slate-100 text-slate-400 border-slate-200">
                        Disabled
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-slate-50 text-slate-300 border-slate-100">
                        Not activated
                      </span>
                    )}
                    {/* Price */}
                    {entry.totalPrice > 0 && (
                      <span className="text-[10px] text-slate-400 font-medium">
                        ${entry.totalPrice.toFixed(0)}
                      </span>
                    )}
                  </div>
                  <p className="text-slate-400 text-xs mt-0.5 truncate">{meta.description}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  {hasRows && (
                    <>
                      {/* Open module */}
                      {MODULE_ROUTES[entry.key] && (
                        <button
                          onClick={() => handleOpenModule(entry)}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-external-link-line text-xs"></i>
                          Open
                        </button>
                      )}
                      {/* Disable / Re-enable */}
                      {entry.isActive ? (
                        <button
                          onClick={() => handleToggle(entry, true)}
                          disabled={isWorking}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-500 hover:bg-red-50 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          {isWorking ? (
                            <span className="w-3 h-3 border border-red-300 border-t-red-500 rounded-full animate-spin"></span>
                          ) : (
                            <i className="ri-forbid-line text-xs"></i>
                          )}
                          Disable
                        </button>
                      ) : (
                        <button
                          onClick={() => handleToggle(entry, false)}
                          disabled={isWorking}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-emerald-200 text-emerald-600 hover:bg-emerald-50 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          {isWorking ? (
                            <span className="w-3 h-3 border border-emerald-300 border-t-emerald-500 rounded-full animate-spin"></span>
                          ) : (
                            <i className="ri-checkbox-circle-line text-xs"></i>
                          )}
                          Re-enable
                        </button>
                      )}
                    </>
                  )}

                  {/* Not activated — info only */}
                  {!hasRows && (
                    <span className="text-slate-300 text-xs italic">Via contract</span>
                  )}
                </div>
              </div>

              {/* Rows detail — only when has rows */}
              {hasRows && (
                <div className="border-t border-slate-100 divide-y divide-slate-50">
                  {entry.rows.map((row) => {
                    const isContract = row.source_label === "contract";
                    const cr = isContract ? (row as ContractAddonRow) : null;

                    return (
                      <div key={row.id} className="flex items-center gap-3 px-5 py-3 bg-slate-50/50">
                        {/* Source icon */}
                        <div className="w-5 h-5 flex items-center justify-center text-slate-400 shrink-0">
                          <i className={isContract ? "ri-file-text-line text-xs" : "ri-user-settings-line text-xs"}></i>
                        </div>

                        {/* Source info */}
                        <div className="flex-1 min-w-0">
                          {isContract && cr ? (
                            <p className="text-slate-600 text-xs truncate">
                              Contract{" "}
                              <span className="font-mono font-semibold text-slate-700">
                                {cr.contract_number ?? cr.contract_id.slice(0, 8)}
                              </span>
                              {cr.contract_title ? ` · ${cr.contract_title}` : ""}
                            </p>
                          ) : (
                            <p className="text-slate-500 text-xs">
                              Manual / Direct
                              {(row as ClientAddonRow).notes ? ` · ${(row as ClientAddonRow).notes}` : ""}
                            </p>
                          )}
                        </div>

                        {/* Source badge */}
                        <SourceBadge row={row} />

                        {/* Price */}
                        <span className="text-slate-500 text-xs font-medium tabular-nums w-12 text-right">
                          {row.price > 0 ? `$${row.price}` : "Free"}
                        </span>

                        {/* Row status */}
                        <span className={`w-2 h-2 rounded-full shrink-0 ${
                          row.status === "active" ? "bg-emerald-400" : "bg-slate-300"
                        }`}></span>

                        {/* Contract link */}
                        {isContract && cr && (
                          <button
                            onClick={() => navigate(`/admin/contracts/${cr.contract_id}`)}
                            className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-[#9a7840] transition-colors cursor-pointer shrink-0"
                            title="View contract"
                          >
                            <i className="ri-arrow-right-s-line text-sm"></i>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <p className="text-slate-400 text-xs text-center pt-2">
        Add-ons are activated from contracts.{" "}
        <span className="text-slate-500 font-medium">Open a contract to activate new add-ons.</span>
      </p>
    </div>
  );
}